import * as yup from 'yup';
import { CAMPAIGN_TEMPLATE_VALUE_TYPES } from 'src/constants/templateValueTypes';
import { convertHtmlToText } from 'src/helpers/helpers';
import { RECIPIENT_AUDIENCE_LABEL } from 'src/modules/campaigns/helpers';
import {
  Campaign,
  CampaignConfigSchema,
  CampaignExternalType,
  CampaignFormValues,
  CampaignPayloadParams,
  CampaignStatus,
  CampaignType,
  FieldKey,
  FormMessage,
  PayloadMessage,
  ScheduleKind,
  ScheduledType,
} from '../types';
import { newClientKey } from '../helpers/clientKey';
import { buildMessageAppointmentPayloadFields } from '../helpers/appointmentMessageHelpers';
import { formatEditorHtmlToMessageText } from 'src/components/TemplateText/helpers';
import { getDefaultOptOutText, isFieldVisible, normalizeLinksWithMimeType } from '../helpers';
import { getGoverningMessageTemplate, resolveMessageType } from '../helpers/messageHelpers';
import { CampaignIssue, mergeIssues, policyIssue, validateSchema } from './issues';
import { hasCaptureErrors } from './shared/primaryActionChecks';

/** A value a campaign type either states outright or derives from its config. */
type Fixed<T> = T | ((config: CampaignConfigSchema) => T);

const resolve = <T>(value: Fixed<T>, config: CampaignConfigSchema): T =>
  (typeof value === 'function' ? (value as (c: CampaignConfigSchema) => T)(config) : value);

export interface RecipientsBehaviour {
  /** Heading for the picker. */
  label: string;
  /** Group types to offer, as the API's comma-separated filter. Undefined = all. */
  groupTypes?: string;
  /** Whether the create-group dialog offers a rules-based dynamic group. */
  allowDynamicGroups: boolean;
  initialGroupType: 'custom' | 'dynamic';
}

/** What a type's `payload` builder is handed. */
export interface PayloadContext {
  values: CampaignFormValues;
  config: CampaignConfigSchema;
  /** Campaign-level fields plus a whitelisted message list. */
  base: CampaignPayloadParams;
  /** `base` plus config-gated groups and keywords, and dateScheduled when set. */
  standard: CampaignPayloadParams;
}

/**
 * What one campaign type does differently. Everything optional falls back to the
 * shared behaviour in `makePolicy` below, so a spec states only its differences.
 */
export interface CampaignTypeSpec {
  externalType: CampaignExternalType;
  /** Which fields the builder renders, in the order it renders them. */
  fields: (config: CampaignConfigSchema) => FieldKey[];
  defaults: (config: CampaignConfigSchema) => CampaignFormValues;
  payload: (context: PayloadContext) => CampaignPayloadParams;
  schema: (config: CampaignConfigSchema) => yup.AnyObjectSchema;
  /** Whether Create/Update/Activate is blocked. */
  issues: (values: CampaignFormValues, config: CampaignConfigSchema) => CampaignIssue[];

  /** Only for the types whose external type depends on their config. */
  externalTypeFor?: (config: CampaignConfigSchema) => CampaignExternalType;
  /** Extra values on top of the shared ones when loading a saved campaign. */
  fromCampaign?: (
    base: CampaignFormValues,
    campaign: Campaign,
    config: CampaignConfigSchema,
  ) => CampaignFormValues;
  /**
   * Messages the API needs even with no copy and no attachment. Everything else
   * is dropped from the payload.
   */
  keepsEmptyMessage?: (message: PayloadMessage, config: CampaignConfigSchema) => boolean;
  /** Which scheduling UI each message renders. */
  scheduleKind?: Fixed<ScheduleKind>;
  /**
   * scheduledType for a message the user adds, when the one before it does not
   * already decide. Reminders and annuals count from the message before.
   */
  defaultScheduledType?: ScheduledType;
  /** Whether the user may add messages beyond the first. */
  allowsMultipleMessages?: Fixed<boolean>;
  /** Reminders carry their appointment at campaign level, not per message. */
  supportsMessageAppointments?: boolean;
  /** Which merge-tag families GET /templates should return. */
  placeholderFamilies?: string;
  /** Whether the builder loads the company's keywords on open. */
  loadsKeywords?: Fixed<boolean>;
  /** How this type treats its audience. Default: outbound, any group, dynamic. */
  recipients?: (config: CampaignConfigSchema) => RecipientsBehaviour;
  /** Copy the blocklist scans before activation. */
  blocklistTexts?: (values: CampaignFormValues) => string[];
}

/** What the rest of the module talks to. */
export interface CampaignPolicy {
  type: CampaignType;
  externalType: CampaignExternalType;
  externalTypeFor: (config: CampaignConfigSchema) => CampaignExternalType;
  fieldManifest: (config: CampaignConfigSchema) => FieldKey[];
  getDefaults: (config: CampaignConfigSchema) => CampaignFormValues;
  fromCampaign: (campaign: Campaign, config: CampaignConfigSchema) => CampaignFormValues;
  toPayload: (
    values: CampaignFormValues,
    config: CampaignConfigSchema,
    status: CampaignStatus,
  ) => CampaignPayloadParams;
  validationSchema: (config: CampaignConfigSchema) => yup.AnyObjectSchema;
  /** Single runtime source for field errors, action gates and submit. */
  validate: (values: CampaignFormValues, config: CampaignConfigSchema) => CampaignIssue[];
  isPrimaryActionDisabled: (values: CampaignFormValues, config: CampaignConfigSchema) => boolean;
  isDraftActionDisabled: (
    values: CampaignFormValues,
    configOrIssues: CampaignConfigSchema | CampaignIssue[],
  ) => boolean;
  messageScheduleKind: (config: CampaignConfigSchema) => ScheduleKind;
  defaultScheduledType: (previous?: FormMessage) => ScheduledType;
  allowsMultipleMessages: (config: CampaignConfigSchema) => boolean;
  supportsMessageAppointments: () => boolean;
  placeholderFamilies: () => string;
  loadsKeywords: (config: CampaignConfigSchema) => boolean;
  recipients: (config: CampaignConfigSchema) => RecipientsBehaviour;
  blocklistTexts: (values: CampaignFormValues) => string[];
}

/** Values every campaign type reads the same way out of a saved campaign. */
function baseFromCampaign(campaign: Campaign, config: CampaignConfigSchema): CampaignFormValues {
  return {
    name: campaign.name,
    groupIds: (campaign.groups ?? []).map(g => g.id),
    keywordIds: (campaign.keywords ?? []).map(k => k.id),
    dateScheduled: campaign.dateScheduled ?? null,
    scheduledMessages: [...(campaign.scheduledMessages ?? [])]
      .sort((a, b) => a.position - b.position)
      .map((m, index) => ({
      clientKey: newClientKey(),
      // Carried so a save edits this message instead of replacing the list.
      id: m.id,
      type: resolveMessageType(getGoverningMessageTemplate(config.messageTemplates, index), m.type),
      text: m.text,
      subject: m.subject,
      links: m.links ?? [],
      // Rebuilt from the legacy `links` array when the backend omits it.
      linksWithMimeType: normalizeLinksWithMimeType(m),
      position: m.position,
      scheduledType: m.scheduledType,
      delay: m.delay,
      time: m.time,
      // Derived from dataCapture, never read off the stored flag: a stored
      // `isCaptureContact: false` sitting next to a real dataCapture would uncheck
      // the capture box and blank its field picker, while the payload went on
      // sending the capture.
      isCaptureContact: !!m.dataCapture,
      dataCapture: m.dataCapture,
      isSurvey: m.isSurvey,
      metadataName: m.metadataName,
      attachCalendar: m.attachCalendar,
      appointmentId: m.appointmentId,
      // Backend edit-lock for this message; drives per-message read-only.
      isEditable: m.isEditable,
      })),
    // Reflects only whether the campaign has opt-out text: the config's `active`
    // flag must not switch it back on.
    optOutText: campaign.optOutText || getDefaultOptOutText(config),
    isOptOutEnabled: Boolean(campaign.optOutText),
  };
}

/**
 * A whitelist, not a spread: the API 400s on any extra field. `clientKey`,
 * `isCaptureContact`, `linksWithMimeType` and `isEditable` are form-only;
 * `metadataName` is annual-only and its own spec adds it.
 */
function payloadMessages(
  values: CampaignFormValues,
  type: CampaignType,
  config: CampaignConfigSchema,
): PayloadMessage[] {
  return values.scheduledMessages.map((m, index) => {
    // A template that pins the type overrides whatever the form is holding.
    const pinned = getGoverningMessageTemplate(config.messageTemplates, index)?.messageType;
    const messageType = pinned === 'mms-only' ? 'mms' : pinned === 'sms-only' ? 'sms' : m.type;
    const isMms = messageType === 'mms';

    return {
      ...(m.id ? { id: m.id } : {}),
      type: messageType,
      // The editor holds HTML; the API takes the message as it will be sent.
      text: formatEditorHtmlToMessageText(m.text) || '',
      // An SMS carries neither. Values left behind by a type switch must not travel.
      subject: isMms ? m.subject : '',
      links: isMms ? m.links : [],
      position: m.position,
      scheduledType: m.scheduledType,
      delay: m.delay,
      time: m.time,
      dataCapture: m.dataCapture,
      isSurvey: m.isSurvey,
      // Emitted for every type except reminder, and cleared when none is attached.
      ...buildMessageAppointmentPayloadFields(
        { appointmentId: m.appointmentId ?? undefined },
        type !== 'reminder',
      ),
    };
  });
}

/**
 * A message with neither copy nor an attachment is not a message. Sending it
 * back would save a blank that the builder then shows again on reload.
 */
const hasContent = (m: PayloadMessage) => !!m.text || (m.links?.length ?? 0) > 0;

/**
 * Turns a spec into the object the rest of the module uses, filling in the
 * shared behaviour for everything the spec leaves out.
 */
export function makePolicy(type: CampaignType, spec: CampaignTypeSpec): CampaignPolicy {
  const configuredExternalTypeFor = spec.externalTypeFor ?? (() => spec.externalType);
  const externalTypeFor = (config: CampaignConfigSchema): CampaignExternalType =>
    ((config as CampaignConfigSchema & { applicationId?: string | null }).applicationId
      ? 'custom'
      : configuredExternalTypeFor(config));

  return {
    type,
    externalType: spec.externalType,
    externalTypeFor,
    fieldManifest: spec.fields,
    getDefaults: spec.defaults,
    validationSchema: spec.schema,

    fromCampaign: (campaign, config) => {
      const base = baseFromCampaign(campaign, config);
      // The old builder always repaired an incomplete saved campaign with the
      // configured defaults (or one blank message). Without this, MessagesField
      // has no row from which the user can add the first message.
      const hydrated = type !== 'text_to_win' && base.scheduledMessages.length === 0
        ? { ...base, scheduledMessages: spec.defaults(config).scheduledMessages }
        : base;
      return spec.fromCampaign ? spec.fromCampaign(hydrated, campaign, config) : hydrated;
    },

    toPayload: (values, config, status) => {
      const base: CampaignPayloadParams = {
        campaignType: type,
        externalType: externalTypeFor(config),
        name: values.name,
        status,
        optOutText: values.isOptOutEnabled ? values.optOutText : null,
        scheduledMessages: payloadMessages(values, type, config)
          .filter(m => hasContent(m) || (spec.keepsEmptyMessage?.(m, config) ?? false)),
      };
      const standard: CampaignPayloadParams = {
        ...base,
        groups: values.groupIds ?? [],
        keywords: isFieldVisible(config.keywords) ? values.keywordIds : undefined,
        ...(values.dateScheduled ? { dateScheduled: values.dateScheduled } : {}),
      };
      return spec.payload({ values, config, base, standard });
    },

    validate: (values, config) => mergeIssues(
      validateSchema(spec.schema(config), values),
      spec.issues(values, config),
      policyIssue(hasCaptureErrors(values), 'capture-field-required', {
        blocksDraft: true,
        blocksPrimaryAction: false,
        message: 'A contact capture field is required',
      }),
      policyIssue(!values.name, 'draft-name-required', {
        path: 'name',
        message: 'Campaign name is required',
        blocksDraft: true,
      }),
    ),

    isPrimaryActionDisabled: (values, config) => mergeIssues(
      validateSchema(spec.schema(config), values),
      spec.issues(values, config),
      policyIssue(hasCaptureErrors(values), 'capture-field-required', {
        blocksDraft: true,
        blocksPrimaryAction: false,
        message: 'A contact capture field is required',
      }),
    ).some(issue => issue.blocksPrimaryAction),

    isDraftActionDisabled: (values, configOrIssues) => mergeIssues(
      Array.isArray(configOrIssues)
        ? configOrIssues
        : validateSchema(spec.schema(configOrIssues), values),
      Array.isArray(configOrIssues) ? [] : spec.issues(values, configOrIssues),
      policyIssue(hasCaptureErrors(values), 'capture-field-required', {
        blocksDraft: true,
        blocksPrimaryAction: false,
        message: 'A contact capture field is required',
      }),
      policyIssue(!values.name, 'draft-name-required', {
        path: 'name',
        message: 'Campaign name is required',
        blocksDraft: true,
      }),
    ).some(issue => issue.blocksDraft),

    messageScheduleKind: config => resolve(spec.scheduleKind ?? 'generic', config),
    // A message that follows one asking for a response waits for that response,
    // whatever the campaign type would otherwise pick.
    defaultScheduledType: previous => (
      previous && (previous.scheduledType === 'reply' || previous.dataCapture || previous.isSurvey)
        ? 'reply'
        : spec.defaultScheduledType ?? 'immediate'),
    allowsMultipleMessages: config => resolve(spec.allowsMultipleMessages ?? true, config),
    supportsMessageAppointments: () => spec.supportsMessageAppointments ?? true,
    placeholderFamilies: () => spec.placeholderFamilies ?? CAMPAIGN_TEMPLATE_VALUE_TYPES,
    loadsKeywords: config => resolve(spec.loadsKeywords ?? isFieldVisible(config.keywords), config),

    recipients: config => spec.recipients?.(config) ?? {
      // An outbound campaign picking recipients from any group.
      label: config.recipients?.label || RECIPIENT_AUDIENCE_LABEL,
      groupTypes: undefined,
      allowDynamicGroups: true,
      initialGroupType: 'dynamic',
    },

    blocklistTexts: values => spec.blocklistTexts?.(values)
      ?? (values.scheduledMessages ?? [])
        .map(m => convertHtmlToText(m.text ?? ''))
        .filter(Boolean),
  };
}

/** Manifest fragment: include `keys` only when the config shows that field. */
export const whenVisible = (
  field: { visible?: boolean } | undefined,
  ...keys: FieldKey[]
): FieldKey[] => (isFieldVisible(field ?? {}) ? keys : []);
